import React, { useMemo, useState } from 'react';
import { farmhouseData } from '../data/mockData';
import { nightShoot } from '../data/media';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import Lightbox from '../components/Lightbox';
import useSEO from '../hooks/useSEO';
import '../styles/studio.css';
import '../styles/night.css';

const pad2 = (n) => String(n).padStart(2, '0');

const gallerySchema = (count) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vengafarms.in/' },
        { '@type': 'ListItem', position: 2, name: 'Photo gallery', item: 'https://vengafarms.in/gallery' },
      ],
    },
    {
      '@type': 'ImageGallery',
      name: 'Photographs of Leoland by VengaFarms',
      description: `${count} photographs of the 4BHK farmhouse, private pool, bedrooms and garden at Shamirpet, Hyderabad.`,
      url: 'https://vengafarms.in/gallery',
      isPartOf: { '@id': 'https://vengafarms.in/#website' },
      about: { '@id': 'https://vengafarms.in/#lodging' },
    },
  ],
});

// The March 2026 night shoot, plus the existing daytime/interior set.
const ALL_IMAGES = [
  ...nightShoot.map((img) => ({
    url: img.src,
    webp: img.webp,
    webpSmall: img.webpSmall,
    title: img.caption,
    category: 'After Dark',
  })),
  ...farmhouseData.gallery.map((img) => ({
    url: img.url,
    title: img.title,
    category: img.category,
  })),
];

const Gallery = () => {
  useSEO({
    title: 'Farmhouse Photos — Pool, Bedrooms & Garden | Leoland, Shamirpet',
    description: `${ALL_IMAGES.length} photographs of our 4BHK farmhouse near Hyderabad — the private pool, the four bedrooms, the garden and the villa lit up after dark at Leonia Resorts, Shamirpet.`,
    path: '/gallery',
  });

  const categories = useMemo(
    () => ['All', ...new Set(ALL_IMAGES.map((img) => img.category))],
    []
  );

  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const filtered = useMemo(() => {
    if (activeCategory === 'All') return ALL_IMAGES;
    return ALL_IMAGES.filter((img) => img.category === activeCategory);
  }, [activeCategory]);

  return (
    <div className="studio">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(gallerySchema(ALL_IMAGES.length)) }}
      />
      <Nav />

      <section className="surface-paper gallery-hero">
        <div className="edge wrap">
          <Reveal>
            <span className="label">The Property, In Full</span>
            <h1 className="display-1" style={{ marginTop: '1rem' }}>Gallery</h1>
            <p className="lede" style={{ maxWidth: '520px', marginTop: '1rem' }}>
              {ALL_IMAGES.length} photographs of the farmhouse &mdash; the villa
              lit up after dark, plus the private pool, the four bedrooms and
              the garden by day.
            </p>

            <div className="filter-row">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`filter-pill ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => { setActiveCategory(cat); setLightboxIndex(null); }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="surface-paper">
        <div className="edge wrap">
          <div className="full-gallery-grid">
            {filtered.map((image, index) => (
              <Reveal
                key={image.url}
                delay={(index % 4) + 1}
                className={`gallery-tile ${index % 5 === 0 ? 'wide' : ''}`}
                onClick={() => setLightboxIndex(index)}
              >
                {image.webp ? (
                  <picture>
                    <source
                      srcSet={`${image.webpSmall} 640w, ${image.webp} 1200w`}
                      sizes="(max-width: 560px) 100vw, (max-width: 900px) 50vw, 33vw"
                      type="image/webp"
                    />
                    <img src={image.url} alt={`${image.title} — Leoland by VengaFarms farmhouse, Shamirpet`} loading="lazy" />
                  </picture>
                ) : (
                  <img src={image.url} alt={`${image.title} — Leoland by VengaFarms farmhouse, Shamirpet`} loading="lazy" />
                )}
                <div className="gallery-tile-meta">
                  <span className="gallery-tile-title">{image.title}</span>
                  <span className="gallery-tile-index">{pad2(index + 1)} &mdash; {image.category}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {lightboxIndex !== null && (
        <Lightbox
          images={filtered}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
};

export default Gallery;
